import React, { useState } from "react";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Box,
  Container,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { styles } from "./AuthPage.styles";
import { auth } from "../../config/firebase";
import { confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";

const PasswordReset = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oobCode = searchParams.get("oobCode");

  // Verify the password reset code and get the email
  React.useEffect(() => {
    const verifyCode = async () => {
      if (oobCode) {
        try {
          const email = await verifyPasswordResetCode(auth, oobCode);
          setEmail(email);
        } catch (err) {
          setError(getFirebaseErrorMessage(err));
        }
      }
    };
    verifyCode();
  }, [oobCode]);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setSuccess(true);
      setTimeout(() => {
        navigate("/auth");
      }, 3000);
    } catch (err) {
      setError(getFirebaseErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const getFirebaseErrorMessage = (error) => {
    const errorCode = error.code;
    const errorMessages = {
      "auth/expired-action-code":
        "Password reset link has expired. Please request a new one.",
      "auth/invalid-action-code":
        "Invalid password reset link. Please request a new one.",
      "auth/weak-password":
        "Password is too weak. Please use a stronger password.",
    };

    return (
      errorMessages[errorCode] ||
      "An unexpected error occurred. Please try again."
    );
  };

  if (!oobCode) {
    return (
      <Box>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={styles.paper}>
            <Typography color="error">
              Invalid password reset link. Please request a new one.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/auth")}
              sx={{ mt: 2 }}
            >
              Back to Sign In
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={styles.paper}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>
            Reset Your Password
          </Typography>

          {error && (
            <Alert severity="error" sx={styles.errorAlert}>
              {error}
            </Alert>
          )}

          {success ? (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Password has been reset successfully! Redirecting to login...
              </Alert>
              <Button
                variant="contained"
                onClick={() => navigate("/auth")}
                sx={{ mt: 2 }}
              >
                Back to Sign In
              </Button>
            </Box>
          ) : (
            <form onSubmit={handleSubmit} style={styles.form}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={email}
                disabled
                margin="normal"
                sx={styles.textField}
                variant="standard"
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                fullWidth
                label="New Password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                sx={styles.textField}
                variant="standard"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm New Password"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                sx={styles.textField}
                variant="standard"
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                sx={styles.submitButton}
                disabled={loading}
              >
                {loading ? "Resetting..." : "Reset Password"}
              </Button>

              <Button
                variant="text"
                onClick={() => navigate("/auth")}
                sx={{ mb: 2 }}
              >
                Back to Sign In
              </Button>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default PasswordReset;
